import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, onMounted, watch } from "vue";
  import Modal from "@/components/ui/Modal.vue";
  import router from "@/router";
  import { formatDate } from "@/helpers/date";
  import { useStore } from "vuex";
  import { formatPrice } from "@/helpers/transformation/price";
  import { LotStatus } from "@/enums/LotStatus";
  import {
    getLotCustomerMapStatusClass,
    getCustomerFacingLotStatusLabel,
  } from "@/helpers/status";
  import BuilderGetStartedTab from "@/components/builder/BuilderGetStartedTab.vue";
  import { BuilderInterfaceWithHouseDesigns } from "@/types/BuilderInterface";
  import BuilderControlsButtons from "@/components/builder/BuilderControlsButtons.vue";
  import { useIsDesktop } from "@/features/useIsDesktop";
  import BuilderLotDocuments from "@/components/builder/BuilderLotDocuments.vue";
  import { Folder } from "lucide-vue-next";

  enum TabsEnum {
    LOT_DETAILS = "lot-details",
    DOCS = "docs",
    HOME_DESIGNS = "home-designs",
  }

  
export default /*@__PURE__*/_defineComponent({
  __name: 'ReserveLotModal',
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    lot: {
      type: Object,
      required: true,
    },
    transitionName: {
      type: String,
      default: "",
    },
    estate: {
      type: Object,
      required: true,
    },
    embedded: {
      type: Boolean,
      default: false,
    },
    businessId: {
      type: String,
      required: false,
      default: undefined,
    },
    estateId: {
      type: String,
      required: false,
      default: undefined,
    },
    buttonsDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: [
    "close",
    "toContact",
    "reserve",
    "hold",
    "toRequestQuote",
    "updateHeaderOverAll",
  ],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

  const { isDesktop } = useIsDesktop(681);

  const store = useStore();

  const emits = __emit;
  const props = __props;

  const loading = ref(false);
  const builders = ref<BuilderInterfaceWithHouseDesigns[]>([]);
  const isInitialHoldDisabled = ref(false);
  const disabledButton = ref(false);
  const estateId = computed(() => getEstateId());

  const activeTabs = ref(TabsEnum.LOT_DETAILS);

  const getEstateId = () => {
    if (props.estateId) {
      return props.estateId;
    } else {
      return router.currentRoute.value.params.estateId;
    }
  };

  const isActiveTab = (tab: TabsEnum) => {
    return activeTabs.value === tab;
  };

  const changeTab = (tab: TabsEnum) => {
    activeTabs.value = tab;
  };

  const landOptionsAvailable = computed(() => {
    const hasBuildersEnabled = props?.lot?.lotId?.isBuildersEnabled;
    const hasBuilders = builders?.value.length;
    const hasHouses = builders.value.every(
      (builder) => builder.houses.length > 0
    );

    return hasBuildersEnabled && hasBuilders && hasHouses;
  });

  const hasHouseAndLandPackage = computed(() => {
    return Boolean(props.lot?.lotId?.isBuildersEnabled);
  });

  const canCreateHomeAndLandPackage = computed(() => {
    return landOptionsAvailable.value && !hasHouseAndLandPackage.value;
  });

  const hasLotDocuments = computed(() => {
    return Boolean(
      props.lot?.lotId?.documents?.length || hasBuilderDocuments.value
    );
  });

  const hasBuilderDocuments = computed(() => {
    return Boolean(
      props.lot?.lotId?.isBuildersEnabled &&
        props.lot?.lotId?.builderDocuments?.length
    );
  });

  const hasAssignedHouseAndLandPackage = computed(() => {
    return hasHouseAndLandPackage.value;
  });

  const assignedBuilderId = computed(() => {
    const builderId = props.lot?.lotId?.builders?.[0]?.builderId;

    return typeof builderId === "object" ? builderId?._id : builderId;
  });

  const assignedBuilder = computed(() => {
    const builderId = props.lot?.lotId?.builders?.[0]?.builderId;

    if (typeof builderId === "object") {
      return builderId;
    }

    return builders.value.find(
      (builder) => builder._id === assignedBuilderId.value
    );
  });

  const assignedBuilderName = computed(() => {
    return assignedBuilder.value?.name || "Home & Land Package";
  });

  const assignedBuilderLogo = computed(() => {
    return hasAssignedHouseAndLandPackage.value
      ? assignedBuilder.value?.logo || ""
      : "";
  });

  const getInitialHoldDisabled = () => {
    disabledButton.value = true;
    store
      .dispatch("estate/getEstate", estateId.value)
      .then((res) => {
        isInitialHoldDisabled.value = !res.data.showInitialHold;
      })
      .finally(() => {
        disabledButton.value = false;
      });
  };
  const getModalClasses = () => {
    const { lot, embedded } = props;
    const reserveClass = hasHouseAndLandPackage.value
      ? "reserve-lot-modal-with-builder"
      : "reserve-lot-modal";
    const documentsClass = lot?.lotId?.documents?.length ? "" : "no-documents";
    const embeddedClass = embedded ? "reserve-lot-modal-embedded" : "";
    const heightClass = "height-limited";

    return `${reserveClass} ${embeddedClass} ${heightClass} ${documentsClass}`;
  };
  const lotId = computed(() => {
    return router.currentRoute.value.query.lotId;
  });

  const urlLinkToEoiRequest = computed(() => {
    return `/estate/${estateId.value}/lot/${lotId.value}/eoi-request?${
      props.businessId ? `businessId=${props.businessId}` : ""
    }`;
  });

  const hold = () => {
    emits("hold", props.lot);
    close();
  };

  function close(needUpdateRoute = true) {
    activeTabs.value = TabsEnum.LOT_DETAILS;
    emits("close", needUpdateRoute);
  }
  const reserve = () => {
    emits("reserve", urlLinkToEoiRequest.value);
    close();
  };

  function toContact() {
    emits("toContact");
    close();
  }
  const onRequestQuote = () => {
    if (!props.buttonsDisabled) {
      router.push({
        name: "Builder",
        params: { lotId: lotId.value as string, estateId: estateId.value },
      });
    }
  };

  const getBuildersByLotId = () => {
    if (!lotId.value) {
      return;
    }

    loading.value = true;
    store
      .dispatch("builder/getBuildersByLotId", lotId.value)
      .then((res) => {
        builders.value = res.data?.builders ?? [];
      })
      .finally(() => {
        loading.value = false;
      });
  };

  onMounted(() => {
    getInitialHoldDisabled();
    getBuildersByLotId();
  });

  watch(lotId, () => {
    getBuildersByLotId();
  });

  watch(
    () => props.isOpen,
    () => {
      emits("updateHeaderOverAll", props.isOpen && !isDesktop.value);
    }
  );

const __returned__ = { isDesktop, store, emits, props, TabsEnum, loading, builders, isInitialHoldDisabled, disabledButton, estateId, activeTabs, getEstateId, isActiveTab, changeTab, landOptionsAvailable, hasHouseAndLandPackage, canCreateHomeAndLandPackage, hasLotDocuments, hasBuilderDocuments, hasAssignedHouseAndLandPackage, assignedBuilderId, assignedBuilder, assignedBuilderName, assignedBuilderLogo, getInitialHoldDisabled, getModalClasses, lotId, urlLinkToEoiRequest, hold, close, reserve, toContact, onRequestQuote, getBuildersByLotId, computed, ref, onMounted, watch, Modal, get router() { return router }, get formatDate() { return formatDate }, get useStore() { return useStore }, get formatPrice() { return formatPrice }, get LotStatus() { return LotStatus }, get getLotCustomerMapStatusClass() { return getLotCustomerMapStatusClass }, get getCustomerFacingLotStatusLabel() { return getCustomerFacingLotStatusLabel }, BuilderGetStartedTab, get BuilderInterfaceWithHouseDesigns() { return BuilderInterfaceWithHouseDesigns }, BuilderControlsButtons, get useIsDesktop() { return useIsDesktop }, BuilderLotDocuments, get Folder() { return Folder } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})